import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ChevronRight, AlertTriangle, MessageCircle } from "lucide-react";
import { useConfig, supportWa } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { useAuth, Spinner } from "@/lib/auth";
import { api, fmtDate } from "@/lib/api";
import { StatusBadge } from "@/components/common";

export default function CustomerDashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const cfg = useConfig();
  useEffect(() => { api.get("/requests/mine").then((r) => setItems(r.data)); }, []);
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="customer-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-teal-700">{t("d.hello", { name: user.name })}</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-1">{t("d.my_requests")}</h1>
        </div>
        <Button asChild className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" data-testid="customer-new-request-btn">
          <Link to="/request"><Plus className="h-4 w-4 me-1" />{t("d.new_request")}</Link>
        </Button>
      </div>
      <div className="mt-8 rounded-3xl bg-slate-900 text-white p-6 md:p-8 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="contact-us-cta">
        <div className="flex-1"><p className="text-xl font-bold">{t("d.contact_us")}</p><p className="text-slate-300 text-sm mt-1">{t("d.contact_us_d")}</p></div>
        <Button asChild className="rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white font-semibold h-11 px-6" data-testid="contact-us-btn">
          <a href={supportWa(cfg, `Hola NicaFix, soy ${user.name}`)} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4 me-2" />{t("d.contact_us")}</a>
        </Button>
      </div>
      <div className="mt-8 space-y-3">
        {items === null && <Spinner />}
        {items?.length === 0 && <p className="text-slate-500 py-10" data-testid="customer-empty">{t("d.empty")}</p>}
        {items?.map((r) => (
          <Link key={r.id} to={`/dashboard/requests/${r.id}`} data-testid={`customer-request-${r.number}`}
            className="lift flex items-center gap-4 rounded-2xl bg-white border border-slate-200 p-5">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm font-semibold text-slate-900">{r.number}</span>
                <StatusBadge status={r.status} />
                {r.urgent && <AlertTriangle className="h-4 w-4 text-red-600" />}
              </div>
              <p className="text-sm text-slate-600 mt-1">{t(r.kind === "cleaning" ? "cl.label" : `svc.${r.service}`)} · {r.city}</p>
              <p className="text-xs text-slate-400 font-mono">{fmtDate(r.created_at)}</p>
            </div>
            <ChevronRight className="h-5 w-5 text-slate-400 rtl:rotate-180" />
          </Link>
        ))}
      </div>
    </div>
  );
}
